import React, { Suspense } from "react";
import { useTranslation } from "react-i18next";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import SINGLE_USER_CONTEXT, { SingleUserProvider } from "src/context/internal/user";
import TabLayout from "src/components/base/tab-layout";
import { Tab, TabMenu } from "src/components/base/tab-menu";
import {
    Dropdown,
    DropdownButton,
    DropdownHeading,
    DropdownItem,
    DropdownLabel,
    DropdownMenu,
    DropdownSection,
} from "src/components/base/dropdown";
import { ChevronDownIcon, ClockIcon, TrashIcon, UserGroupIcon } from "@heroicons/react/20/solid";
import { SimpleUser } from "src/api/generated/frontend_internal_v1";
import ExpireUserDevices from "src/components/dialogs/expire-user-devices";
import AddToGroupDialog from "src/components/dialogs/add-to-group";
import DeleteLocalUserDialog from "src/components/dialogs/delete-local-user";
import { TenantBadge } from "src/components/badges/tenant";
import Outlet from "src/components/base/outlet";

/**
 * The properties for {@link SingleUser}
 */
export type SingleUserProps = {};

/**
 * The menu for a single user
 */
export default function SingleUser(props: SingleUserProps) {
    const [t] = useTranslation("admin-user-overview");

    const navigate = useNavigate();
    const { userId } = Route.useParams();

    const [openAddUserToGroup, setOpenAddUserToGroup] = React.useState<SimpleUser | undefined>(undefined);
    const [openExpireDevices, setOpenExpireDevices] = React.useState<SimpleUser | undefined>(undefined);
    const [openDelete, setOpenDelete] = React.useState<SimpleUser | undefined>(undefined);

    return (
        <SingleUserProvider uuid={userId}>
            <SINGLE_USER_CONTEXT.Consumer>
                {(ctx) => (
                    <>
                        <TabLayout
                            breadcrumbs={[
                                {
                                    label: t("button.user"),
                                    to: "/a/users",
                                    search: { filter: "all", search: "", tenant: "all", page: 1 },
                                },
                                {
                                    label: ctx.user.display_name,
                                    to: "/a/users/$userId/general",
                                    params: { userId: ctx.user.uuid },
                                },
                            ]}
                            heading={t("heading.user", { user: ctx.user.display_name })}
                            headingMeta={<TenantBadge tenant={ctx.user.tenant} />}
                            headingChildren={
                                <Dropdown>
                                    <DropdownButton outline={true}>
                                        {t("button.edit")}
                                        <ChevronDownIcon />
                                    </DropdownButton>
                                    <DropdownMenu>
                                        <DropdownSection>
                                            <DropdownItem onClick={() => setOpenAddUserToGroup({ ...ctx.user })}>
                                                <UserGroupIcon />
                                                <DropdownLabel>{t("button.add-to-group")}</DropdownLabel>
                                            </DropdownItem>
                                        </DropdownSection>
                                        <DropdownSection>
                                            <DropdownHeading>{t("heading.danger-zone")}</DropdownHeading>
                                            <DropdownItem onClick={() => setOpenExpireDevices({ ...ctx.user })}>
                                                <ClockIcon />
                                                <DropdownLabel>{t("button.expire-user")}</DropdownLabel>
                                            </DropdownItem>
                                            <DropdownItem onClick={() => setOpenDelete({ ...ctx.user })}>
                                                <TrashIcon />
                                                <DropdownLabel>{t("button.delete-user")}</DropdownLabel>
                                            </DropdownItem>
                                        </DropdownSection>
                                    </DropdownMenu>
                                </Dropdown>
                            }
                            tabs={
                                <TabMenu>
                                    <Tab href={"/a/users/$userId/general"}>{t("label.general")}</Tab>
                                    {ctx.user.auth === "Invited" && (
                                        <Tab href={"/a/users/$userId/invitation"}>{t("label.invitation")}</Tab>
                                    )}
                                    {ctx.user.auth === "Local" && (
                                        <Tab href={"/a/users/$userId/access-history"}>{t("label.access-history")}</Tab>
                                    )}
                                </TabMenu>
                            }
                        >
                            <Outlet />
                        </TabLayout>

                        <Suspense>
                            <AddToGroupDialog
                                isOpen={!!openAddUserToGroup}
                                onClose={() => setOpenAddUserToGroup(undefined)}
                                onSubmit={() => {
                                    setOpenAddUserToGroup(undefined);
                                    ctx.reset();
                                }}
                                user={openAddUserToGroup}
                            />

                            <ExpireUserDevices
                                onClose={() => setOpenExpireDevices(undefined)}
                                onExpire={() => setOpenExpireDevices(undefined)}
                                isOpen={!!openExpireDevices}
                                user={openExpireDevices}
                            />

                            <DeleteLocalUserDialog
                                simpleUser={openDelete}
                                isOpen={!!openDelete}
                                onClose={() => setOpenDelete(undefined)}
                                onDelete={async () => {
                                    setOpenDelete(undefined);
                                    await navigate({
                                        to: "/a/users",
                                        search: { search: "", filter: "all", tenant: "all", page: 1 },
                                    });
                                }}
                            />
                        </Suspense>
                    </>
                )}
            </SINGLE_USER_CONTEXT.Consumer>
        </SingleUserProvider>
    );
}
export const Route = createFileRoute("/_admin/a/users/$userId/_user")({
    component: SingleUser,
});
